import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, CheckCircle2, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import { trainerApi, type StudentSummary, type StudentDetail, type StudentWork } from '@/lib/api/trainer';
import { StudentWorkTab } from './StudentWorkTab';
import { queryKeys } from '@/lib/queryKeys';
import { cn } from '@/lib/cn';

type Tab = 'work' | 'progress' | 'attendance';

interface Props {
  student: StudentSummary;
  batchId: string;
  open: boolean;
  onClose: () => void;
}

function statusBadgeClass(pct: number) {
  if (pct >= 70) return 'bg-green-100 text-green-700';
  if (pct >= 30) return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

function statusBadgeLabel(pct: number) {
  if (pct >= 70) return 'On Track';
  if (pct >= 30) return 'At Risk';
  return 'Behind';
}

function attendanceStatusChip(status: string | null) {
  if (!status) return <span className="text-xs text-[#6b7280]">—</span>;
  const map: Record<string, string> = {
    present: 'bg-green-100 text-green-700',
    late: 'bg-amber-100 text-amber-700',
    absent: 'bg-red-100 text-red-700',
  };
  return (
    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium capitalize', map[status] ?? '')}>
      {status}
    </span>
  );
}

export function StudentDetailSheet({ student, batchId, open, onClose }: Props) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('work');
  const [flagged, setFlagged] = useState(student.is_flagged);
  const [messageOpen, setMessageOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);

  const { data: detail, isLoading, isError } = useQuery<StudentDetail>({
    queryKey: ['studentDetail', batchId, student.id],
    queryFn: async () => {
      const { data } = await trainerApi.getStudentDetail(batchId, student.id);
      return data;
    },
    enabled: open,
  });

  const { data: work, isLoading: workLoading } = useQuery<StudentWork>({
    queryKey: ['studentWork', batchId, student.id],
    queryFn: async () => {
      const { data } = await trainerApi.getStudentWork(batchId, student.id);
      return data;
    },
    enabled: open,
  });

  const flagMutation = useMutation({
    mutationFn: (next: boolean) => trainerApi.toggleFlag(batchId, student.id, next),
    onMutate: (next) => setFlagged(next),
    onError: (_err, next) => {
      setFlagged(!next);
      toast.error('Failed to update flag');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.trainer.batchOverview(batchId) });
    },
  });

  const messageMutation = useMutation({
    mutationFn: () => trainerApi.notifyStudent(batchId, student.id, message),
    onSuccess: () => {
      setSent(true);
      setMessage('');
      setTimeout(() => {
        setSent(false);
        setMessageOpen(false);
      }, 2000);
    },
    onError: () => toast.error('Failed to send message'),
  });

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto p-0">
        {/* Header */}
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#ececec]">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-[#f3f4f6] text-sm font-semibold">
              {student.avatar_url
                ? <img src={student.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                : student.username[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <SheetTitle className="text-base font-semibold leading-none m-0">{student.username}</SheetTitle>
                <span className={cn('inline-block rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 translate-y-px', statusBadgeClass(student.progress_pct))}>
                  {statusBadgeLabel(student.progress_pct)}
                </span>
              </div>
            </div>
            <button
              onClick={() => flagMutation.mutate(!flagged)}
              disabled={flagMutation.isPending}
              className="rounded p-1.5 hover:bg-[#fafafa] disabled:opacity-50 mr-6"
              aria-label={flagged ? 'Remove flag' : 'Flag for follow-up'}
            >
              <Flag className={cn('h-4 w-4', flagged ? 'fill-orange-500 text-orange-500' : 'text-[#6b7280]')} />
            </button>
          </div>

          {/* Message area */}
          <div className="mt-3">
            <button
              onClick={() => setMessageOpen((v) => !v)}
              className="flex items-center gap-1.5 text-xs font-medium text-[#ff5a1f] hover:underline"
            >
              Message student
              {messageOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {messageOpen && (
              <div className="mt-2">
                {sent ? (
                  <p className="text-xs text-green-600 font-medium">Sent ✓</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder="Type a message…"
                      className="w-full rounded-md border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#ff5a1f] resize-none"
                    />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-[#6b7280]">{message.length}/500</span>
                      <button
                        onClick={() => messageMutation.mutate()}
                        disabled={!message.trim() || messageMutation.isPending}
                        className="rounded-md bg-[#ff5a1f] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                      >
                        {messageMutation.isPending ? 'Sending…' : 'Send'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetHeader>

        {/* Tabs */}
        <div className="flex border-b border-[#ececec] px-5">
          {(['work', 'progress', 'attendance'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'mr-4 py-3 text-sm font-medium capitalize border-b-2 -mb-px transition-colors',
                tab === t
                  ? 'border-[#ff5a1f] text-[#111827]'
                  : 'border-transparent text-[#6b7280] hover:text-[#111827]',
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="px-5 py-4">
          {isLoading && (
            <div className="flex h-40 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#ff5a1f] border-t-transparent" />
            </div>
          )}
          {isError && (
            <p className="py-8 text-center text-sm text-[#6b7280]">Failed to load student data.</p>
          )}

          {!isLoading && detail && tab === 'progress' && (
            <ProgressTab topics={detail.topics} />
          )}
          {!isLoading && detail && tab === 'attendance' && (
            <AttendanceTab sessions={detail.attendance} />
          )}
          {tab === 'work' && (
            workLoading
              ? <div className="flex h-40 items-center justify-center">
                  <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#ff5a1f] border-t-transparent" />
                </div>
              : work
                ? <StudentWorkTab work={work} />
                : <p className="py-8 text-center text-sm text-[#6b7280]">Failed to load this student&apos;s work.</p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function ProgressTab({ topics }: { topics: StudentDetail['topics'] }) {
  if (topics.length === 0) {
    return <p className="text-sm text-[#6b7280]">No topics in this batch.</p>;
  }
  // Group by module_title
  const groups = topics.reduce<Record<string, typeof topics>>((acc, t) => {
    (acc[t.module_title] ??= []).push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-5">
      {Object.entries(groups).map(([module, items]) => (
        <div key={module}>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#6b7280]">{module}</p>
          <div className="space-y-1.5">
            {items.map((t) => (
              <div key={t.topic_id} className="flex items-center gap-2.5">
                {t.completed
                  ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-500" />
                  : <Circle className="h-4 w-4 flex-shrink-0 text-[#9ca3af]" />}
                <span className={cn('text-sm', t.completed ? 'text-[#111827]' : 'text-[#6b7280]')}>
                  {t.topic_title}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function AttendanceTab({ sessions }: { sessions: StudentDetail['attendance'] }) {
  if (sessions.length === 0) {
    return <p className="text-sm text-[#6b7280]">No sessions in the last 90 days.</p>;
  }
  return (
    <div className="space-y-2">
      {sessions.map((s) => (
        <div key={s.session_id} className="flex items-center justify-between rounded-lg border border-[#ececec] px-3 py-2.5">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-[#111827]">{s.session_title}</p>
            <p className="text-xs text-[#6b7280]">
              {new Date(s.scheduled_at).toLocaleDateString('en-US', {
                weekday: 'short', month: 'short', day: 'numeric',
              })}
            </p>
          </div>
          {attendanceStatusChip(s.status)}
        </div>
      ))}
    </div>
  );
}
